import { Query, Question } from '@jupiterone/jupiter-types/question';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { useEffect, useState } from 'react';
import { JupiterTheme } from '../../theme/types';
import {
  getQueryTemplateVariables,
  injectVariablesIntoQuery,
} from '../util/queryTemplate';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    templateForm: {
      marginTop: theme.spacing(4),
    },
    textField: {
      marginRight: theme.spacing(1),
    },
    query: {
      color: theme.palette.navy[500],
      fontFamily: `'Ubuntu Mono', monospace`,
      whiteSpace: 'pre-wrap',
    },
  })
);

interface Props {
  open: boolean;
  question: Question;
  onSubmit: (queries: Query[]) => void;
  onCancel: VoidFunction;
}

interface FormProps {
  query: string;
  onChange: (modifiedQuery: string) => void;
}

export const QueryTemplateForm = ({ query, onChange }: FormProps) => {
  const classes = useStyles();
  const [variables, setVariables] = useState<string[]>([]);
  const [values, setValues] = useState<string[]>([]);
  const [modifiedQuery, setModifiedQuery] = useState<string>(query);

  useEffect(() => {
    setModifiedQuery(query);
    setVariables(getQueryTemplateVariables(query));
  }, [query]);

  useEffect(() => {
    const valueMap = variables.reduce((acc, v, i) => {
      acc[v] = values[i] || `{{${v}}}`;
      return acc;
    }, {} as { [v: string]: string });
    const newQuery = injectVariablesIntoQuery(query, valueMap);
    setModifiedQuery(newQuery);
    onChange(newQuery);
  }, [values]);

  const updateValue = (index: number) => (e: any) => {
    const newValues = [...values];
    newValues[index] = e.target.value;
    setValues(newValues);
  };

  if (variables.length === 0) return null;

  return (
    <div className={classes.templateForm}>
      <Typography variant="body1" className={classes.query}>
        {modifiedQuery}
      </Typography>
      {variables.map((v, i) => (
        <TextField
          className={classes.textField}
          key={v}
          label={v}
          onChange={updateValue(i)}
        />
      ))}
    </div>
  );
};

export const PopulateTemplateDialog = ({
  open,
  question,
  onSubmit,
  onCancel,
}: Props) => {
  const [modifiedQueries, setModifiedQueries] = useState<Query[]>(
    question.queries
  );
  const handleChange = (index: number) => (modifiedQuery: string) => {
    const newQueries = [...modifiedQueries];
    newQueries[index] = { ...newQueries[index], query: modifiedQuery };
    setModifiedQueries(newQueries);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      onSubmit(modifiedQueries);
    }
  };
  return (
    <Dialog open={open} onKeyPress={handleKeyPress}>
      <DialogTitle>{question.title}</DialogTitle>
      <DialogContent>
        <Typography variant="caption">
          This question contains queries using template variables. Fill in the
          values for each variable to continue.
        </Typography>
        {question.queries.map(({ query }, i) => (
          <QueryTemplateForm
            key={query}
            query={query}
            onChange={handleChange(i)}
          />
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} variant="outlined">
          Cancel
        </Button>
        <Button onClick={() => onSubmit(modifiedQueries)} variant="outlined">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PopulateTemplateDialog;
